<template>
    <div>
        <b-alert :show="!!errorHandlingMessage" variant="danger" dismissible>{{ errorHandlingMessage }}</b-alert>
        <div class="mb-4 d-flex align-items-start justify-content-between">
            <div class="">
                <h3 class="mb-3">{{ $t('vendorTagSettings.title') }}</h3>
                <div class="text-muted font-size-80">
                    <p class="mb-3">
                        {{ $t('vendorTagSettings.introTracking') }}
                    </p>
                    <p class="mb-0">
                        {{ $t('vendorTagSettings.introVisibility') }}
                    </p>
                </div>
            </div>
            <div class="min-width-250 text-right">
                <b-button
                    variant="gray-success"
                    size="lg"
                    @click="startCreate(resourceName)"
                >
                    <b-icon icon="plus-circle" class="mr-2"></b-icon>
                    {{ $t('vendorTagSettings.newCategory') }}
                </b-button>
            </div>
        </div>
        <div
            v-if="!loading && !hasCategories"
            class="w-100 height-300 d-flex align-items-center justify-content-center border rounded-lg bg-gray-100"
        >
            <div class="text-muted text-center">
                <div class="font-size-100 font-weight-bold text-uppercase">{{ $t('vendorTagSettings.noCategories') }}</div>
                <div class="">{{ $t('vendorTagSettings.clickToAdd') }}</div>
            </div>
        </div>
        <div 
            v-for="cat in categories"
            :key="cat.id"
            class="mb-4"
        >
            <vendor-tag-category
                :categoryId="cat.id"
                @deleted="getCategories"
            ></vendor-tag-category>
            <!-- add some event handlers to refresh, etc. -->
        </div>

        <b-modal
            v-model="modalShowing"
            centered
            :title="$t('vendorTagSettings.createCategoryTitle')"
            body-class="bg-gray-100"
            footer-class="bg-gray-100"
            :ok-title="$t('buttons.save')"
            button-size="lg"
            @hidden="onModalHidden"
            @cancel="modalShowing = false"
            @ok="triggerFormSubmit"
        >
            <FormulateForm
                :name="resourceName"
                :schema="formSchema"
                :values="formInitialData"
                @submit="onFormSubmit"
            ></FormulateForm>            
        </b-modal>

    </div>
</template>

<script>
import { errorHandling } from '@/js/custom-vue-mixins';
import { getResourceApi } from '@/js/api-service';
import VendorTagCategory from './VendorTagCategory.vue';
import cloneDeep from 'lodash/cloneDeep';
import invoke from 'lodash/invoke';

export default {
    name: '',
    components: {
        VendorTagCategory,
    },
    mixins: [
        errorHandling,
    ],
    i18n: {
        messages: {
            en: {
                vendorTagSettings: {
                    title: 'Vendor Categories & Capabilities',
                    introTracking: 'Use Vendor Categories to track various things about your vendors, including their capabilities, internal status, preferred types of projects, and more. We call these Vendor Tags.',
                    introVisibility: 'For each Category, you can choose to have it as internal-only (not visible to vendors at all), visible to vendors, or visible and editable by vendors.',
                    newCategory: 'New Category',
                    noCategories: 'No Categories',
                    clickToAdd: 'Click above to add one',
                    createCategoryTitle: 'Create Category',
                    createdToast: 'Successfully created category',
                    createError: 'Error while creating category',
                    schemaError: 'Could not get {name} schema',
                },
            },
            es: {
                vendorTagSettings: {
                    title: 'Categorías y Capacidades de Proveedores',
                    introTracking: 'Use las Categorías de Proveedores para dar seguimiento a diversos aspectos de sus proveedores, incluidas sus capacidades, estado interno, tipos de proyectos preferidos y más. Las llamamos Etiquetas de Proveedor.',
                    introVisibility: 'Para cada Categoría, puede elegir que sea solo interna (no visible para los proveedores), visible para los proveedores, o visible y editable por los proveedores.',
                    newCategory: 'Nueva Categoría',
                    noCategories: 'Sin Categorías',
                    clickToAdd: 'Haga clic arriba para agregar una',
                    createCategoryTitle: 'Crear Categoría',
                    createdToast: 'Categoría creada correctamente',
                    createError: 'Error al crear la categoría',
                    schemaError: 'No se pudo obtener el esquema de {name}',
                },
            },
            pt: {
                vendorTagSettings: {
                    title: 'Categorias e Capacidades de Fornecedores',
                    introTracking: 'Use as Categorias de Fornecedores para acompanhar diversos aspectos dos seus fornecedores, incluindo suas capacidades, status interno, tipos de projetos preferidos e muito mais. Nós as chamamos de Etiquetas de Fornecedor.',
                    introVisibility: 'Para cada Categoria, você pode escolher mantê-la apenas interna (não visível para os fornecedores), visível para os fornecedores, ou visível e editável pelos fornecedores.',
                    newCategory: 'Nova Categoria',
                    noCategories: 'Sem Categorias',
                    clickToAdd: 'Clique acima para adicionar uma',
                    createCategoryTitle: 'Criar Categoria',
                    createdToast: 'Categoria criada com sucesso',
                    createError: 'Erro ao criar a categoria',
                    schemaError: 'Não foi possível obter o esquema de {name}',
                },
            },
            'zh-Hans': {
                vendorTagSettings: {
                    title: '供应商类别和能力',
                    introTracking: '使用供应商类别来跟踪供应商的各种信息，包括其能力、内部状态、偏好的项目类型等。我们称之为供应商标签。',
                    introVisibility: '对于每个类别，您可以选择将其设为仅限内部（供应商完全不可见）、供应商可见、或供应商可见且可编辑。',
                    newCategory: '新建类别',
                    noCategories: '暂无类别',
                    clickToAdd: '点击上方添加一个',
                    createCategoryTitle: '创建类别',
                    createdToast: '已成功创建类别',
                    createError: '创建类别时出错',
                    schemaError: '无法获取 {name} 架构',
                },
            },
            'zh-Hant': {
                vendorTagSettings: {
                    title: '供應商類別和能力',
                    introTracking: '使用供應商類別來追蹤供應商的各種資訊，包括其能力、內部狀態、偏好的專案類型等。我們稱之為供應商標籤。',
                    introVisibility: '對於每個類別，您可以選擇將其設為僅限內部（供應商完全看不到）、供應商可見、或供應商可見且可編輯。',
                    newCategory: '新增類別',
                    noCategories: '尚無類別',
                    clickToAdd: '點選上方新增一個',
                    createCategoryTitle: '建立類別',
                    createdToast: '已成功建立類別',
                    createError: '建立類別時發生錯誤',
                    schemaError: '無法取得 {name} 結構描述',
                },
            },
        },
    },
    props: {
        // no props... just get all categories for the tenant
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            loading: false,
            saving: false,
            categories: [],
            resourceName: 'CompanyTagCategory',  // fixed
            // form / modal stuff
            modalShowing: false,
            formInitialData: null,
            formSchema: null,
        }
    },
    computed: {
        api() {
            return getResourceApi(this.resourceName);
        },
        hasCategories() {
            return !!this.categories && Array.isArray(this.categories) && this.categories.length;
        },
    },
    mounted() {
        this.getCategories();
        this.getSchema();
    },
    methods: {
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
        async getCategories() {
            this.loading = true;
            let resp;
            try {
                resp = await this.api.list();
            } catch (error) {
                this.apiErrorHandler(error);
            }
            if (resp?.data) {
                this.categories = resp.data;
            }
            this.loading = false;
        },
        onModalHidden() {
            this.resetForm();
        },
        resetForm() {
            this.formInitialData = {};
            this.formSchema = [];
        },
        async startCreate(resourceName) {
            console.log('Start Create', resourceName);
            const schema = await this.getSchema(resourceName);
            this.openModal({schema});
        },
        openModal({data = {}, schema}) {
            // generic way to start create/edit
            // set things first, then open modal
            this.formInitialData = cloneDeep(data);
            this.formSchema = cloneDeep(schema);
            // finally, open the modal
            this.modalShowing = true;
        },
        triggerFormSubmit() {
            // called from the modal 'ok' button - submit form programmatically
            // we have 2 forms, but only one at a time, form name is this.formResourceName
            this.$formulate.submit(this.resourceName);
        },
        async onFormSubmit(submitData) {
            // called from @submit of the form - do the submit, return promise
            console.log('START OF onFormSubmit', submitData);
            this.saving = true;
            let resp;
            const extraSubmitData = {};  // might need later...
            const finalData = Object.assign(submitData, extraSubmitData);
            //const apiAction = isEdit ? api.partialUpdate : api.create;
            const apiActionName = 'create';
            const actionArgs = [finalData];
            console.log('finalData', finalData);
            console.log('apiActionName', apiActionName);
            console.log('actionArgs', actionArgs);
            try {
                // NOPE resp = await apiAction(...actionArgs);
                resp = await invoke(this.api, apiActionName, ...actionArgs);
                console.log(resp.data);
                this.$toast.success(this.$t('vendorTagSettings.createdToast'));
            } catch (error) {
                this.apiErrorHandler(error, this.$t('vendorTagSettings.createError'));
            }
            // success message / toast
            this.modalShowing = false;  // close modal - which will reset form on @hidden
            // REFRESH THINGS
            this.getCategories();
            this.saving = false;
        },
        async getSchema() {
            // get schema and stash on this.formSchema
            // only works for endpoints that use 'ResourceViewsetEndpoint'
            let response;
            try {
                response = await this.api.getSchema();
            } catch (error) {
                this.apiErrorHandler(error, this.$t('vendorTagSettings.schemaError', { name: this.resourceName }));
            }
            const rawSchema = response.data;
            // stash it in our 'schema store'
            this.formSchema = rawSchema;
            return this.formSchema;
        },
    }
}
</script>

<style>

</style>